import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal } from "@/components/motion/Reveal";
import { PILLARS, IMAGES } from "@/lib/content";
import { Check } from "lucide-react";

function PillarSection({ p, index }) {
  const light = index % 2 === 0;
  const flipped = index % 2 === 1;
  return (
    <section
      id={p.slug}
      data-testid={`focus-${p.slug}`}
      className={`scroll-mt-24 py-20 md:py-28 ${light ? "bg-ptf-bg" : "bg-ptf-primary text-ptf-bg"}`}
    >
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flipped ? "lg:[&>*:first-child]:order-2" : ""}`}>
          <Reveal>
            <div className="relative aspect-[4/3] overflow-hidden group">
              <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-105" loading="lazy" />
              <span className={`absolute top-5 left-5 font-display text-6xl ${light ? "text-ptf-bg" : "text-ptf-accent"} drop-shadow-lg`}>{p.num}</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-4 ${light ? "text-ptf-primary/50" : "text-ptf-accent"}`}>Area {p.num}</p>
            <h2 className={`font-display font-extrabold uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight ${light ? "text-ptf-primary" : ""}`}>{p.title}</h2>
            <p className={`mt-6 leading-relaxed ${light ? "text-ptf-text/75" : "text-ptf-bg/75"}`}>{p.description}</p>
            <p className={`mt-4 font-serif-ed italic text-lg ${light ? "text-ptf-secondary" : "text-ptf-accent"}`}>{p.intended}</p>

            <div className="mt-8">
              <p className={`text-[0.65rem] uppercase tracking-[0.22em] mb-4 ${light ? "text-ptf-primary/50" : "text-ptf-bg/50"}`}>Potential programme directions</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {p.directions.map((d) => (
                  <li key={d} className="flex items-start gap-2.5 text-sm">
                    <Check className={`h-4 w-4 mt-0.5 shrink-0 ${light ? "text-ptf-secondary" : "text-ptf-accent"}`} />
                    <span className={light ? "text-ptf-text/75" : "text-ptf-bg/75"}>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`mt-8 pt-6 border-t ${light ? "border-ptf-primary/15" : "border-ptf-bg/15"}`}>
              <p className={`text-[0.65rem] uppercase tracking-[0.22em] mb-2 ${light ? "text-ptf-primary/50" : "text-ptf-bg/50"}`}>Partnership opportunities</p>
              <p className={`text-sm leading-relaxed ${light ? "text-ptf-text/70" : "text-ptf-bg/70"}`}>{p.partnerships}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Focus() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.replace("#", ""));
    if (!el) return;
    const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <Seo title="Our Focus" path="/focus" description="The eight areas of work in Planet Trotter Foundation's objects: conservation, responsible tourism, communities, education, sustainable agriculture, health and water, youth and innovation, in India and worldwide." />
      <PageHero
        eyebrow="Our Focus"
        titleLines={["The work,", "in eight parts."]}
        intro="The Foundation's objects under the Companies Act cover eight areas. This page sets out what each one means in practice, and where partners fit in."
        image={IMAGES.forest}
      />

      {PILLARS.map((p, i) => (
        <PillarSection key={p.slug} p={p} index={i} />
      ))}

      <section className="bg-ptf-secondary text-ptf-bg py-24 md:py-32 text-center">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <h2 className="font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tight leading-[0.92]">See an area you want to build?</h2>
          <p className="mt-6 max-w-xl mx-auto text-ptf-bg/75">Let's explore how a partnership could turn intent into impact.</p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <PtfButton to="/partnerships" variant="accent">Partner With Us</PtfButton>
            <PtfButton to="/programmes" variant="outlineLight">View Programmes</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
